/**
 * The surface manifests, fetched once and shared by every page.
 *
 * Nothing here keeps a copy of the manifests. They are read from
 * GET /agent-atlas/api/v1/surfaces the first time any page asks, cached for the
 * session, and derived into the lists the Atlas pages use.
 *
 * If the request fails the status is 'error' and the lists are EMPTY — never a
 * bundled guess. A page that needs a surface must show its error state then:
 * an alerts filter built from an empty mapping would quietly widen to every
 * alert, which is worse than saying the definitions could not be loaded.
 * SurfacesGate does that for a whole page.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { agentAtlasAPI } from '../../services/api/agentAtlasClient';
import {
  IDLE, getState, readyState, resetState, setState, subscribe,
} from './store';

let inflight = null;

/** Start the fetch if nothing has, and return the shared promise. */
export const loadSurfaces = () => {
  if (getState().status === 'ready') return Promise.resolve(getState());
  if (inflight) return inflight;
  setState({ ...IDLE, status: 'loading' });
  inflight = agentAtlasAPI.getSurfaceManifests()
    .then((body) => {
      const next = readyState(body);
      if (next.surfaces.length === 0) {
        throw new Error('The surface registry came back empty.');
      }
      setState(next);
      return getState();
    })
    .catch((error) => {
      setState({ ...IDLE, status: 'error', error });
      return getState();
    })
    .finally(() => { inflight = null; });
  return inflight;
};

/** Forget what was loaded. Tests use it; so does retry after an error. */
export const resetSurfaces = () => {
  inflight = null;
  resetState();
};

/**
 * { status, surfaces, fingerprint, lists, error, retry }
 * status: 'idle' | 'loading' | 'ready' | 'error'
 * lists:  { SURFACE_KEYS, ALERT_SURFACE_KEYS, FINOPS_SURFACE_KEYS, ADOPTION_SURFACE_KEYS,
 *           MODULE_ROUTES, SURFACE_TO_ALERT_MODULES } — empty until 'ready'
 */
export const useSurfaces = () => {
  const snapshot = useSyncExternalStore(subscribe, getState, getState);
  useEffect(() => {
    if (snapshot.status === 'idle') loadSurfaces();
  }, [snapshot.status]);
  return {
    ...snapshot,
    retry: () => { resetSurfaces(); return loadSurfaces(); },
  };
};
